import { grpcClientFactory } from './grpcClientFactory';
import { protoArtifactToTs, protoFolderToTs } from './wireShapes';
import { GrpcCallError } from '../utils/grpcErrors';
class UserFolderService {
    createAuthenticatedClient() {
        return grpcClientFactory.createAuthenticatedUserProjectsClient({ enableCompression: true });
    }
    getAuthMetadata() {
        const sessionId = localStorage.getItem('landscope_session_id');
        const sessionToken = localStorage.getItem('landscope_session_token');
        if (!sessionId || !sessionToken) {
            throw new Error('User must be authenticated');
        }
        return { authorization: `Bearer ${sessionId}|${sessionToken}` };
    }
    async createFolder(request) {
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.CreateUserFolderRequest;
                const grpcReq = new Req();
                grpcReq.setProjectId(request.projectId);
                grpcReq.setName(request.name);
                if (request.color)
                    grpcReq.setColor(request.color);
                // Empty string is treated as root on the server (same as the field
                // being unset). Only set the field when the caller specified a parent.
                if (request.parentFolderId)
                    grpcReq.setParentFolderId(request.parentFolderId);
                const client = this.createAuthenticatedClient();
                const metadata = this.getAuthMetadata();
                client.createUserFolder(grpcReq, metadata, (err, resp) => {
                    if (err) {
                        reject(new Error(`Failed to create folder: ${err.message}`));
                        return;
                    }
                    if (!resp) {
                        reject(new Error('No response received'));
                        return;
                    }
                    const folder = resp.getFolder();
                    if (!folder) {
                        reject(new Error('Folder not returned in response'));
                        return;
                    }
                    resolve(protoFolderToTs(folder));
                });
            }
            catch (e) {
                reject(new Error(`Failed to create folder: ${e}`));
            }
        });
    }
    async getFolders(projectId) {
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.GetUserFoldersRequest;
                const grpcReq = new Req();
                grpcReq.setProjectId(projectId);
                const client = this.createAuthenticatedClient();
                const metadata = this.getAuthMetadata();
                client.getUserFolders(grpcReq, metadata, (err, resp) => {
                    if (err) {
                        reject(new GrpcCallError(`Failed to get folders: ${err.message}`, err.code));
                        return;
                    }
                    if (!resp) {
                        reject(new Error('No response received'));
                        return;
                    }
                    const foldersList = resp.getFoldersList() || [];
                    resolve(foldersList.map(protoFolderToTs));
                });
            }
            catch (e) {
                reject(new Error(`Failed to get folders: ${e}`));
            }
        });
    }
    async getFolder(folderId) {
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.GetUserFolderRequest;
                const grpcReq = new Req();
                grpcReq.setFolderId(folderId);
                const client = this.createAuthenticatedClient();
                const metadata = this.getAuthMetadata();
                client.getUserFolder(grpcReq, metadata, (err, resp) => {
                    if (err) {
                        reject(new Error(`Failed to get folder: ${err.message}`));
                        return;
                    }
                    if (!resp) {
                        reject(new Error('No response received'));
                        return;
                    }
                    const folder = resp.getFolder();
                    if (!folder) {
                        reject(new Error('Folder not found'));
                        return;
                    }
                    const artifactsList = resp.getArtifactsList() || [];
                    resolve({
                        ...protoFolderToTs(folder),
                        artifacts: artifactsList.map(protoArtifactToTs)
                    });
                });
            }
            catch (e) {
                reject(new Error(`Failed to get folder: ${e}`));
            }
        });
    }
    async updateFolder(request) {
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.UpdateUserFolderRequest;
                const grpcReq = new Req();
                grpcReq.setFolderId(request.folderId);
                if (request.name !== undefined)
                    grpcReq.setName(request.name);
                if (request.color !== undefined)
                    grpcReq.setColor(request.color);
                // Tri-state: undefined = leave alone, null = move to root, string = move under that id.
                // Setting to "" on the wire is how we encode "move to root" (the server's
                // parentFolderIDForUpdate maps empty-but-present back to nil parent).
                if (request.parentFolderId !== undefined) {
                    grpcReq.setParentFolderId(request.parentFolderId === null ? '' : request.parentFolderId);
                }
                const client = this.createAuthenticatedClient();
                const metadata = this.getAuthMetadata();
                client.updateUserFolder(grpcReq, metadata, (err, _resp) => {
                    if (err) {
                        reject(new Error(`Failed to update folder: ${err.message}`));
                        return;
                    }
                    resolve();
                });
            }
            catch (e) {
                reject(new Error(`Failed to update folder: ${e}`));
            }
        });
    }
    async deleteFolder(folderId) {
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.DeleteUserFolderRequest;
                const grpcReq = new Req();
                grpcReq.setFolderId(folderId);
                const client = this.createAuthenticatedClient();
                const metadata = this.getAuthMetadata();
                client.deleteUserFolder(grpcReq, metadata, (err, _resp) => {
                    if (err) {
                        reject(new Error(`Failed to delete folder: ${err.message}`));
                        return;
                    }
                    resolve();
                });
            }
            catch (e) {
                reject(new Error(`Failed to delete folder: ${e}`));
            }
        });
    }
    async reorderFolders(projectId, folderIds, parentFolderId) {
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.ReorderUserFoldersRequest;
                const grpcReq = new Req();
                grpcReq.setProjectId(projectId);
                grpcReq.setFolderIdsList(folderIds);
                // Only set the parent field when reordering a non-root sibling set.
                // Server treats unset / empty as "root siblings".
                if (parentFolderId)
                    grpcReq.setParentFolderId(parentFolderId);
                const client = this.createAuthenticatedClient();
                const metadata = this.getAuthMetadata();
                client.reorderUserFolders(grpcReq, metadata, (err, _resp) => {
                    if (err) {
                        reject(new Error(`Failed to reorder folders: ${err.message}`));
                        return;
                    }
                    resolve();
                });
            }
            catch (e) {
                reject(new Error(`Failed to reorder folders: ${e}`));
            }
        });
    }
    async createArtifact(request) {
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.CreateUserArtifactRequest;
                const grpcReq = new Req();
                grpcReq.setFolderId(request.folderId);
                grpcReq.setArtifactType(request.artifactType);
                grpcReq.setName(request.name);
                grpcReq.setColor(request.color);
                if (request.displayUnit)
                    grpcReq.setDisplayUnit(request.displayUnit);
                grpcReq.setGeometryJson(request.geometryJson);
                if (request.icon !== undefined)
                    grpcReq.setIcon(request.icon);
                if (request.showLabel !== undefined)
                    grpcReq.setShowLabel(request.showLabel);
                const client = this.createAuthenticatedClient();
                const metadata = this.getAuthMetadata();
                client.createUserArtifact(grpcReq, metadata, (err, resp) => {
                    if (err) {
                        reject(new Error(`Failed to create artifact: ${err.message}`));
                        return;
                    }
                    if (!resp) {
                        reject(new Error('No response received'));
                        return;
                    }
                    const artifact = resp.getArtifact();
                    if (!artifact) {
                        reject(new Error('Artifact not returned in response'));
                        return;
                    }
                    resolve(protoArtifactToTs(artifact));
                });
            }
            catch (e) {
                reject(new Error(`Failed to create artifact: ${e}`));
            }
        });
    }
    async getArtifact(artifactId) {
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.GetUserArtifactRequest;
                const grpcReq = new Req();
                grpcReq.setUserArtifactId(artifactId);
                const client = this.createAuthenticatedClient();
                const metadata = this.getAuthMetadata();
                client.getUserArtifact(grpcReq, metadata, (err, resp) => {
                    if (err) {
                        reject(new Error(`Failed to get artifact: ${err.message}`));
                        return;
                    }
                    if (!resp) {
                        reject(new Error('No response received'));
                        return;
                    }
                    const artifact = resp.getArtifact();
                    if (!artifact) {
                        reject(new Error('Artifact not found'));
                        return;
                    }
                    resolve(protoArtifactToTs(artifact));
                });
            }
            catch (e) {
                reject(new Error(`Failed to get artifact: ${e}`));
            }
        });
    }
    async updateArtifact(request) {
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.UpdateUserArtifactRequest;
                const grpcReq = new Req();
                grpcReq.setUserArtifactId(request.artifactId);
                if (request.name !== undefined)
                    grpcReq.setName(request.name);
                if (request.color !== undefined)
                    grpcReq.setColor(request.color);
                if (request.displayUnit !== undefined)
                    grpcReq.setDisplayUnit(request.displayUnit);
                if (request.geometryJson !== undefined)
                    grpcReq.setGeometryJson(request.geometryJson);
                if (request.folderId !== undefined)
                    grpcReq.setFolderId(request.folderId);
                if (request.icon !== undefined)
                    grpcReq.setIcon(request.icon);
                if (request.showLabel !== undefined)
                    grpcReq.setShowLabel(request.showLabel);
                const client = this.createAuthenticatedClient();
                const metadata = this.getAuthMetadata();
                client.updateUserArtifact(grpcReq, metadata, (err, _resp) => {
                    if (err) {
                        reject(new Error(`Failed to update artifact: ${err.message}`));
                        return;
                    }
                    resolve();
                });
            }
            catch (e) {
                reject(new Error(`Failed to update artifact: ${e}`));
            }
        });
    }
    async deleteArtifact(artifactId) {
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.DeleteUserArtifactRequest;
                const grpcReq = new Req();
                grpcReq.setUserArtifactId(artifactId);
                const client = this.createAuthenticatedClient();
                const metadata = this.getAuthMetadata();
                client.deleteUserArtifact(grpcReq, metadata, (err, _resp) => {
                    if (err) {
                        reject(new Error(`Failed to delete artifact: ${err.message}`));
                        return;
                    }
                    resolve();
                });
            }
            catch (e) {
                reject(new Error(`Failed to delete artifact: ${e}`));
            }
        });
    }
    async reorderArtifacts(folderId, artifactIds) {
        return new Promise((resolve, reject) => {
            try {
                const Req = proto.landscope.mapviewer.v1.ReorderUserArtifactsRequest;
                const grpcReq = new Req();
                grpcReq.setFolderId(folderId);
                grpcReq.setArtifactIdsList(artifactIds);
                const client = this.createAuthenticatedClient();
                const metadata = this.getAuthMetadata();
                client.reorderUserArtifacts(grpcReq, metadata, (err, _resp) => {
                    if (err) {
                        reject(new Error(`Failed to reorder artifacts: ${err.message}`));
                        return;
                    }
                    resolve();
                });
            }
            catch (e) {
                reject(new Error(`Failed to reorder artifacts: ${e}`));
            }
        });
    }
}
export const userFolderService = new UserFolderService();
